import React, { useMemo, useState, useEffect } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { StopRecord, Meter, MeterUnit } from '../../types';
import Card from '../common/Card';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { generateAIAnalysis, AIAnalysisResult } from '../../services/aiAnalysis';

interface ChartData {
  id: string;
  name: string;
  consumo: number;
  unit: string;
}

interface ConsumptionChartProps {
  selectedRecords: StopRecord[];
  meters: Meter[];
}

const StatBox: React.FC<{ title: string; value: string; unit: string; }> = ({ title, value, unit }) => (
  <div className="bg-white border border-gray-100 p-5 rounded-2xl text-center shadow-sm hover:shadow-md transition-shadow">
    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-2">{title}</p>
    <p className="text-3xl font-black text-brand-primary">
      {value}
      <span className="text-lg font-medium text-gray-400 ml-1">{unit}</span>
    </p>
  </div>
);

const ConsumptionChart: React.FC<ConsumptionChartProps> = ({ selectedRecords, meters }) => {
  const [chartError, setChartError] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysisResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    setChartError(null);
    setAiAnalysis(null);
  }, [selectedRecords, meters]);

  const handleGenerateAnalysis = async () => {
    try {
      setIsAnalyzing(true);
      const result = await generateAIAnalysis(selectedRecords, meters);
      setAiAnalysis(result);
    } catch (error) {
      console.error("Error generating analysis", error);
      setChartError("No se pudo generar el análisis de IA.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getMeterUnit = (meterId: string) => meters.find(m => m.id === meterId)?.unit || '';
  const getMeterName = (meterId: string) => meters.find(m => m.id === meterId)?.name || 'N/A';

  const [viewMode, setViewMode] = useState<'total' | 'hourly'>('total');

  const units = new Set(selectedRecords.map(r => getMeterUnit(r.meterId)));
  const areUnitsConsistent = units.size <= 1;
  const displayUnit = areUnitsConsistent ? (Array.from(units)[0] || '') : 'Mixto';

  const data: ChartData[] = useMemo(() => {
    try {
      return selectedRecords
        .filter(record => {
          return record.endValue !== null &&
            record.endValue !== undefined &&
            record.startValue !== null &&
            record.startValue !== undefined &&
            !isNaN(record.endValue) &&
            !isNaN(record.startValue);
        })
        .map(record => {
          const consumption = record.endValue! - record.startValue;
          const durationHours = (record.endTime! - record.startTime) / (1000 * 60 * 60);
          const hourlyRate = durationHours > 0 ? consumption / durationHours : 0;

          return {
            id: record.id,
            name: `${getMeterName(record.meterId)}\n${new Date(record.startTime).toLocaleDateString()}`,
            consumo: Math.max(0, viewMode === 'total' ? consumption : hourlyRate),
            unit: viewMode === 'total' ? getMeterUnit(record.meterId) : `${getMeterUnit(record.meterId)}/h`,
          };
        });
    } catch (error) {
      console.error('Error processing chart data:', error);
      setChartError('Error al procesar los datos del gráfico');
      return [];
    }
  }, [selectedRecords, meters, viewMode, getMeterName, getMeterUnit]); // Added getMeterName, getMeterUnit to dependencies

  const legendName = areUnitsConsistent
    ? (viewMode === 'total' ? `Consumo Total (${displayUnit})` : `Carga Base (${displayUnit}/h)`)
    : 'Consumo';

  const { minConsumo, maxConsumo } = useMemo(() => {
    if (data.length === 0) return { minConsumo: 0, maxConsumo: 0 };
    const values = data.map(d => d.consumo).filter(c => !isNaN(c) && isFinite(c));
    if (values.length === 0) return { minConsumo: 0, maxConsumo: 0 };
    return {
      minConsumo: Math.min(...values),
      maxConsumo: Math.max(...values)
    };
  }, [data]);

  const getBarColor = (value: number) => {
    if (maxConsumo === minConsumo) return '#F4B400';
    if (value === minConsumo) return 'rgb(0, 230, 118)'; // Verde Neón (Valor más bajo)
    if (value === maxConsumo) return 'rgb(255, 23, 68)'; // Rojo Neón (Valor más alto)
    return 'rgb(255, 214, 0)'; // Naranja amarillento (Valores intermedios)
  };

  const stats = useMemo(() => {
    try {
      if (data.length === 0 || !areUnitsConsistent) {
        return { min: '-', max: '-', avg: '-', range: '-' };
      }

      const consumptions = data.map(d => d.consumo).filter(c => !isNaN(c) && isFinite(c));

      if (consumptions.length === 0) {
        return { min: '-', max: '-', avg: '-', range: '-' };
      }

      const min = Math.min(...consumptions);
      const max = Math.max(...consumptions);
      const avg = consumptions.reduce((a, b) => a + b, 0) / consumptions.length;
      const range = max - min;

      const format = (n: number) => {
        if (!isFinite(n) || isNaN(n)) return '-';
        return n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      };

      return {
        min: format(min),
        max: format(max),
        avg: format(avg),
        range: format(range)
      };
    } catch (error) {
      console.error('Error calculating stats:', error);
      return { min: '-', max: '-', avg: '-', range: '-' };
    }
  }, [data, areUnitsConsistent]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const pointData = payload[0].payload;
      const unit = pointData.unit || '';
      const value = payload[0].value;
      const pointName = pointData.name || label;

      if (isNaN(value) || !isFinite(value)) {
        return null;
      }

      return (
        <div className="bg-[#181b1f]/95 backdrop-blur-sm p-4 border border-[#2c3035] rounded-xl shadow-2xl">
          <p className="font-bold text-gray-200 whitespace-pre-line text-sm mb-2">{pointName}</p>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#00E5FF]"></div>
            <p className="text-gray-400 font-medium text-sm">
              {viewMode === 'hourly' ? 'Carga Base:' : 'Consumo:'} <span className="text-[#00E5FF] font-bold text-lg">{value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> <span className="text-gray-500 text-xs">{unit}</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (chartError && !aiAnalysis) {
    return (
      <Card>
        <h2 className="text-xl font-bold text-text-primary mb-4">Análisis Comparativo de Consumo</h2>
        <div className="h-[400px] flex items-center justify-center bg-red-50 rounded-2xl border border-red-100">
          <div className="text-center p-6 bg-white rounded-xl shadow-sm">
            <p className="text-red-600 font-bold mb-2">Error al generar gráfico</p>
            <p className="text-gray-500 text-sm mb-4">{chartError}</p>
            <button
              onClick={() => setChartError(null)}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Card className="overflow-hidden border-t-4 border-t-[#E2B53F]">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Análisis Comparativo de Consumo</h2>
            <p className="text-sm text-gray-500 mt-1">Comparativa visual del consumo en paradas seleccionadas</p>
          </div>
          <div className="flex items-center gap-3">
            {areUnitsConsistent && data.length > 0 && (
              <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1.5 rounded-full border border-green-200 shadow-sm">
                Datos Normalizados ({displayUnit})
              </span>
            )}

            <div className="flex items-center bg-gray-100 rounded-lg p-1 shadow-inner">
              <button
                onClick={() => setViewMode('total')}
                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${viewMode === 'total' ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Absoluto
              </button>
              <button
                onClick={() => setViewMode('hourly')}
                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${viewMode === 'hourly' ? 'bg-[#F46800] text-white shadow' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Carga Base (p/hora)
              </button>
            </div>
          </div>
        </div>

        <div className="h-[400px] w-full bg-[#27272A] rounded-2xl p-4 border border-[#3f3f46] mt-4 shadow-2xl relative">
          {selectedRecords.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#3f3f46" />
                <XAxis
                  dataKey="id"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#d1d5db' }}
                  dy={15}
                  tickFormatter={(id) => {
                    const item = data.find(d => d.id === id);
                    return item ? item.name : id;
                  }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#d1d5db' }}
                  tickFormatter={(value) => {
                    if (isNaN(value) || !isFinite(value)) return '0';
                    return value.toLocaleString();
                  }}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }} />
                <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ color: '#f3f4f6' }} />
                <Bar
                  dataKey="consumo"
                  name={legendName}
                  radius={[6, 6, 0, 0]}
                  maxBarSize={60}
                  animationDuration={1500}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor(entry.consumo)} />
                  ))}
                  <LabelList
                    dataKey="consumo"
                    position="insideBottom"
                    fill="#000000"
                    fontSize={10}
                    fontWeight="bold"
                    dy={-8}
                    formatter={(value: number) => {
                      if (!value || isNaN(value)) return '';
                      return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
                    }}
                  />
                </Bar>
                <Line
                  type="monotone"
                  dataKey="consumo"
                  name="Tendencia de Consumo"
                  stroke="#00E5FF"
                  strokeWidth={2.5}
                  strokeDasharray="6 4"
                  dot={{ r: 4, fill: '#00E5FF', strokeWidth: 1 }}
                  activeDot={{ r: 6, fill: '#00E5FF' }}
                  animationDuration={1500}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center p-6">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 border border-gray-200">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <p className="text-gray-900 font-bold mb-1">Sin datos para mostrar</p>
              <p className="text-sm text-gray-500 max-w-sm">Seleccione registros históricos en la tabla inferior para visualizar su consumo en este gráfico.</p>
            </div>
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatBox title="Mínimo" value={stats.min} unit={areUnitsConsistent ? (viewMode === 'total' ? displayUnit : `${displayUnit}/h`) : ''} />
            <StatBox title="Máximo" value={stats.max} unit={areUnitsConsistent ? (viewMode === 'total' ? displayUnit : `${displayUnit}/h`) : ''} />
            <StatBox title="Promedio" value={stats.avg} unit={areUnitsConsistent ? (viewMode === 'total' ? displayUnit : `${displayUnit}/h`) : ''} />
            <StatBox title="Rango Dif." value={stats.range} unit={areUnitsConsistent ? (viewMode === 'total' ? displayUnit : `${displayUnit}/h`) : ''} />
          </div>
          {!areUnitsConsistent && data.length > 0 &&
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-4 rounded-xl mt-6 flex items-start sm:items-center shadow-sm">
              <span className="mr-3 text-xl bg-white p-1 rounded-full shadow-sm">⚠️</span>
              <p>Las estadísticas consolidadas solo se calculan cuando todos los registros seleccionados comparten la misma unidad de medida (KWH o M3). Selecciona solo elementos del mismo tipo para comparar equitativamente.</p>
            </div>
          }
        </div>
      </Card>

      {!aiAnalysis && selectedRecords.length > 0 && (
        <div className="flex justify-center mt-2 mb-6">
          <button
            onClick={handleGenerateAnalysis}
            disabled={isAnalyzing}
            className={`flex items-center justify-center gap-3 px-8 py-4 rounded-2xl text-base font-bold shadow-lg transition-all w-full md:w-auto ${isAnalyzing
              ? 'bg-[#F4B400]/50 text-gray-900/60 cursor-wait'
              : 'bg-[#F4B400] hover:bg-[#DBA200] text-gray-900 hover:shadow-xl transform hover:-translate-y-1 active:translate-y-0'
              }`}
          >
            {isAnalyzing ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
            ) : (
              <SparklesIcon className="h-6 w-6 text-gray-900" />
            )}
            {isAnalyzing ? "Generando Análisis Integral..." : "Generar Análisis de Carga Base"}
          </button>
        </div>
      )}

      {aiAnalysis && (
        <Card className={`border-t-4 transition-all duration-500 animate-in slide-in-from-bottom-4 ${aiAnalysis.isAnomaly ? 'border-t-red-500' : 'border-t-[#E2B53F]'} relative overflow-hidden`}>
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <SparklesIcon className="h-32 w-32" />
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-xl ${aiAnalysis.isAnomaly ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-[#C49826]'}`}>
                <SparklesIcon className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Análisis Inteligente de Modulación</h3>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm mb-6">
              <p className={`text-lg font-medium leading-relaxed ${aiAnalysis.isAnomaly ? 'text-red-700' : 'text-gray-800'}`}>
                {aiAnalysis.summary}
              </p>

              {/* Score Indicator */}
              <div className="mt-6">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-sm font-bold text-gray-600 uppercase tracking-wider">Estabilidad de Carga Base</span>
                  <span className="text-2xl font-black text-gray-900">{aiAnalysis.cargaBaseScore.toFixed(0)}<span className="text-sm text-gray-500 font-medium">/100</span></span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-1000 ${aiAnalysis.cargaBaseScore > 80 ? 'bg-green-500' : aiAnalysis.cargaBaseScore > 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${aiAnalysis.cargaBaseScore}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider ml-1">Detalles Interpretados:</h4>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aiAnalysis.insights.map((insight, index) => (
                  <li key={index} className="flex items-start bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-brand-primary/10 flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-brand-primary text-xs font-bold">{index + 1}</span>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">{insight}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ConsumptionChart;